import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, catchError, forkJoin, map, of, tap } from 'rxjs';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';

import { LOG_MODULE, LoggerService } from '../logging';

import { I18nNamespaceRegistry } from './i18n-namespace.registry';

@Injectable()
export class MultiTranslateHttpLoader implements TranslateLoader {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly registry: I18nNamespaceRegistry = inject(
    I18nNamespaceRegistry,
  );
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly basePath: string = '/i18n';

  public getTranslation(lang: string): Observable<TranslationObject> {
    const pages: readonly string[] = this.registry.list();

    if (pages.length === 0) {
      return of({});
    }
    const requests: Observable<TranslationObject>[] = pages.map((page) =>
      this.fetchPage(page, lang),
    );

    return forkJoin(requests).pipe(
      map((parts) =>
        parts.reduce<TranslationObject>(
          (acc, part) => ({ ...acc, ...part }),
          {},
        ),
      ),
      tap(() => this.registry.markFetched(lang, pages)),
    );
  }

  private fetchPage(
    page: string,
    lang: string,
  ): Observable<TranslationObject> {
    return this.http
      .get<TranslationObject>(`${this.basePath}/${page}/${lang}.json`)
      .pipe(
        map((dict) => ({ [this.toNamespace(page)]: dict })),
        catchError((error: unknown) => {
          this.logger.error('Translation namespace failed to load', error, {
            module: LOG_MODULE.i18n,
            page,
            lang,
          });
          return of<TranslationObject>({});
        }),
      );
  }

  private toNamespace(page: string): string {
    return page.replace(/-/g, '_').toUpperCase();
  }
}
